import { useState } from "react";
import { Alert, Button, Code, Group, Stack, Table, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";

import {
  fetchPortalReportAttachment,
  listPortalReportAttachments,
  PDF,
  type PortalReportAttachment,
} from "@/api/reportAttachments";
import { config } from "@/config";
import { t } from "@/i18n";

/**
 * The lab's original attachment on the review view (Gate B M2 S16): what the lab sent with
 * this report, usually its own PDF, so the doctor can check the report against it.
 *
 * Renders nothing, and asks for nothing, unless onboarding-api says the routes exist
 * (`portalReportAttachmentsEnabled`). The list carries no title (a lab can put the
 * patient's name there), so a row is labelled by what it is. Opening one is a separate
 * audited read on the server. A PDF opens in a new tab, anything else downloads: the bytes
 * reach this page as a Blob whose type the API module forces, so a file the lab sent is
 * never rendered as a page of ours.
 */
export function LabAttachments({ reportId }: { reportId: string }): JSX.Element | null {
  if (!config.portalReportAttachmentsEnabled) return null;
  return <LabAttachmentList reportId={reportId} />;
}

function LabAttachmentList({ reportId }: { reportId: string }): JSX.Element {
  const [openFailed, setOpenFailed] = useState(false);
  const { data, isPending, isError } = useQuery({
    queryKey: ["portal-report-attachments", reportId],
    queryFn: () => listPortalReportAttachments(reportId),
    retry: false,
    refetchOnWindowFocus: false,
  });

  async function open(attachment: PortalReportAttachment): Promise<void> {
    setOpenFailed(false);
    let blob: Blob;
    try {
      blob = await fetchPortalReportAttachment(reportId, attachment.index);
    } catch {
      setOpenFailed(true);
      return;
    }
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.rel = "noopener";
    if (blob.type === PDF) {
      link.target = "_blank";
    } else {
      link.download = `attachment-${attachment.index}`;
    }
    link.click();
    // Long enough for the new tab or the download to take the bytes.
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  return (
    <section data-testid="review-attachments">
      <Title order={2} mt="xl" mb="sm">
        {t("portal.report_attachments")}
      </Title>
      {isPending ? (
        <Text c="dimmed">{t("portal.report_attachments_loading")}</Text>
      ) : isError ? (
        <Alert color="orange">{t("portal.report_attachments_unavailable")}</Alert>
      ) : data.attachments.length === 0 ? (
        <Text c="dimmed">{t("portal.report_attachments_empty")}</Text>
      ) : (
        <Stack gap="xs">
          {openFailed ? (
            <Alert color="red" data-testid="review-attachment-open-failed">
              {t("portal.report_attachments_open_failed")}
            </Alert>
          ) : null}
          <Table withTableBorder>
            <Table.Tbody>
              {data.attachments.map((attachment) => (
                <Table.Tr key={attachment.index} data-testid="review-attachment">
                  <Table.Td>
                    <Text fw={attachment.original ? 600 : undefined}>
                      {attachment.original
                        ? t("portal.report_attachments_original")
                        : `${t("portal.report_attachments_other")} ${attachment.index}`}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Code>{attachment.content_type}</Code>
                  </Table.Td>
                  <Table.Td>{`${Math.max(1, Math.round(attachment.size / 1024))} KB`}</Table.Td>
                  <Table.Td>
                    <Group justify="flex-end">
                      <Button size="xs" variant="light" onClick={() => void open(attachment)}>
                        {attachment.content_type.trim().toLowerCase() === PDF
                          ? t("portal.report_attachments_open")
                          : t("portal.report_attachments_download")}
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      )}
    </section>
  );
}
